// ─────────────────────────────────────────────
// Oike — the commercial facts of a quote (Diagnostic Intake), kept apart.
// Each fact is confirmed only by its own field: an approved quote is not a held
// meeting, a published proposal is not a sent one, and a won opportunity is not
// a received payment nor a started service. When there is only an indirect
// signal, the fact stays "pendiente de confirmar": never an invented date.
// Fields and their status in Airtable: docs/solicitudes/pipeline.md.
// Shared by ConsultingPipeline (browser) and scripts/test-pipeline-facts.mjs.
// ─────────────────────────────────────────────

/** The new fields of Diagnostic Intake (central base), created by Omni. Written only when PIPELINE_FACT_WRITES is on. */
export const FACT_FIELDS = {
  quoteApprovedAt: 'Quote Approved At',
  meetingStatus: 'Meeting Status',       // Single select: Scheduled, Held, Cancelled
  meetingAt: 'Meeting At',               // Date with time (UTC): when it was (or is) scheduled
  meetingHeldAt: 'Meeting Held At',      // Date with time (UTC): confirmed held
  proposalSentAt: 'Proposal Sent At',    // Date with time (UTC)
  proposalSentVia: 'Proposal Sent Via',  // Single line text: email, whatsapp, meeting…
  wonAt: 'Won At',                       // Date with time (UTC)
  paymentStatus: 'Payment Status',       // Single select: Pending, Partial, Received
  paymentReceivedAt: 'Payment Received At',
  serviceStartedAt: 'Service Started At',
  factsNote: 'Pipeline Facts Note',      // Long text: who confirmed what, when it was registered late
};

/**
 * Writes of the new fields: on since Ale confirmed (28 Oct 2026) that Omni created
 * the 11 fields and that Meeting Status and Payment Status have no default option.
 * Each fact is written only when Ale registers it, with the date she gives; nothing
 * rewrites historical records. Approving stamps Quote Approved At with the moment
 * it is approved. Off, the pipeline only reads (docs/solicitudes/pipeline.md).
 */
export const PIPELINE_FACT_WRITES = true;

/**
 * An acceptance corrected through quote-correction.mts leaves one of these marks in
 * Pipeline Facts Note, on the line that says what it was, what it is, who, when and why.
 * - Error: the acceptance was registered by mistake → back to Approved, Won At cleared
 *   (kept on the line), it no longer counts as won.
 * - Cancelled: the client accepted and later cancelled → Lost, Won At kept (it is true
 *   history); shown as "cancelada", never as won.
 * Either way the content stays locked (src/quoteRules.js): a change is a new revision.
 */
export const ACCEPTANCE_ERROR_MARK = '[aceptación corregida: registrada por error]';
export const ACCEPTANCE_CANCEL_MARK = '[aceptación cancelada por el cliente]';
export const acceptanceMarksOf = (note) => ({
  error: String(note || '').includes(ACCEPTANCE_ERROR_MARK),
  cancelled: String(note || '').includes(ACCEPTANCE_CANCEL_MARK),
});

export const MEETING_STATUSES = ['Scheduled', 'Held', 'Cancelled'];
export const PAYMENT_STATUSES = ['Pending', 'Partial', 'Received'];

/** The columns, in order. A record sits in the furthest column whose fact is confirmed. */
export const PIPELINE_STAGES = [
  { key: 'intake-sent', label: 'Formulario enviado', color: '#6b7280', description: 'Link compartido; falta la respuesta.' },
  { key: 'intake-filled', label: 'Formulario completo', color: '#2D5F99', description: 'La persona completó el formulario previo.' },
  { key: 'quote-draft', label: 'Cotización en preparación', color: '#6B5CA5', description: 'Cotización interna en borrador.' },
  { key: 'quote-approved', label: 'Cotización aprobada', color: '#7A5AA0', description: 'Aprobada internamente. No implica reunión.' },
  { key: 'meeting-held', label: 'Reunión realizada', color: '#9A6212', description: 'Reunión confirmada como realizada.' },
  { key: 'proposal-published', label: 'Propuesta publicada', color: '#A0521A', description: 'La página existe. No implica que se haya enviado.' },
  { key: 'proposal-sent', label: 'Propuesta enviada', color: '#B4462A', description: 'Envío confirmado.' },
  { key: 'won', label: 'Ganada', color: '#1E7B4F', description: 'Aceptó la propuesta. Pago e inicio se registran aparte.' },
  { key: 'cancelled', label: 'Cancelada por el cliente', color: '#6b7280', description: 'Aceptó y después canceló. No cuenta como ganada; su historia, pagos y servicios quedan.' },
];

const STAGE_INDEX = Object.fromEntries(PIPELINE_STAGES.map((s, i) => [s.key, i]));

const has = (v) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0);
const dateOf = (v) => (typeof v === 'string' && v && !Number.isNaN(Date.parse(v)) ? v : '');
const statusName = (v) => (v && typeof v === 'object' ? v.name : v) || '';

// ── When a fact happened: a day chosen without a time, or an exact moment ──
// The date fields are "date with time". A day without a time is stored as that day at
// 12:00 UTC (Airtable then shows the right day anywhere in the Americas and Europe) and
// marked in Pipeline Facts Note as "<field>: solo el día YYYY-MM-DD". Oike reads that
// mark and shows the day as chosen, with no time and no time zone conversion; the 12:00
// is only a carrier, never shown as the time of the fact. With a time, it is a moment.
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
export const DAY_ONLY_MARK = 'solo el día';
const DAY_LINE_RE = /^(.+?): solo el día (\d{4}-\d{2}-\d{2})\b/;

/** Today in the browser's own calendar (YYYY-MM-DD): the limit for facts that already happened. */
export function localToday(now = new Date()) {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/** field name → day, for the dates marked as "solo el día" in Pipeline Facts Note. */
export function dayOnlyFieldsOf(note) {
  const out = new Map();
  for (const line of String(note || '').split('\n')) { const m = line.trim().match(DAY_LINE_RE); if (m) out.set(m[1], m[2]); }
  return out;
}

/** The note with the "solo el día" line of one field replaced (day) or removed (null). Other lines stay. */
export function noteWithDay(note, field, day) {
  const lines = String(note || '').split('\n').filter(l => l.trim() && !(l.trim().startsWith(`${field}: ${DAY_ONLY_MARK} `)));
  if (day) lines.push(`${field}: ${DAY_ONLY_MARK} ${day} (sin hora; la hora que muestra Airtable no es real)`);
  return lines.join('\n');
}

/** { at, precision }: a marked day ('day', at = YYYY-MM-DD) or a moment ('moment', at = ISO). A stale mark (the field was edited later) counts as a moment. */
function whenOf(f, field, dayOnly) {
  const v = dateOf(f[field]);
  if (!v) return { at: '', precision: '' };
  const day = dayOnly.get(field);
  return day && v.slice(0, 10) === day ? { at: day, precision: 'day' } : { at: v, precision: 'moment' };
}

/** How the screen writes a date: a day exactly as chosen (no conversion), a moment in the viewer's time zone with its time. */
export function formatWhen(at, precision, { timeZone } = {}) {
  if (!at) return '';
  if (precision === 'day' || DAY_RE.test(at)) {
    const [y, m, d] = at.slice(0, 10).split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
  }
  return new Date(at).toLocaleString('es', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', ...(timeZone ? { timeZone } : {}) });
}

/** A chosen day, with an optional time (HH:MM, in the viewer's time zone): what to store and whether it is only a day. */
function encodeWhen(at, time) {
  if (DAY_RE.test(String(at || ''))) {
    if (time) {
      if (!/^\d{2}:\d{2}$/.test(time)) throw new Error('La hora no es válida');
      const d = new Date(`${at}T${time}`); // the viewer's own time zone: the moment she lived
      if (Number.isNaN(d.getTime())) throw new Error('La fecha no es válida');
      return { iso: d.toISOString(), day: null, moment: d };
    }
    return { iso: `${at}T12:00:00.000Z`, day: at, moment: null };
  }
  if (!at || Number.isNaN(Date.parse(at))) throw new Error('Falta la fecha');
  return { iso: new Date(at).toISOString(), day: null, moment: new Date(at) };
}

function quoteHasLines(json) {
  try { return JSON.parse(json || '[]').some(l => String(l?.service || '').trim()); } catch { return false; }
}

/**
 * Meetings logged in the activity of the same account ("Meeting Scheduled" in
 * Outreach): an indirect signal, never a confirmation that it was held.
 */
export function scheduledMeetingsOf(accountId, outreach = []) {
  if (!accountId) return [];
  return outreach.filter(o => {
    const f = o.fields || {};
    const acc = Array.isArray(f['Account']) ? f['Account'] : [];
    return acc.includes(accountId) && (statusName(f['Status']) === 'Meeting Scheduled' || statusName(f['Channel']) === 'Meeting');
  });
}

/**
 * The facts of one quote. Each one: { state, at, source }
 * - state 'confirmed': its own field says so (at is that field's date, or '' when the field has no date yet)
 * - state 'pending': only an indirect signal; it has to be confirmed (at is always '')
 * - state 'none': nothing says it happened
 * `landing` is the linked Consulting Landing record, when loaded (its Published At is the publication date).
 */
export function pipelineFactsOf(fields = {}, { landing = null, outreach = [] } = {}) {
  const f = fields || {};
  const qs = statusName(f['Quote Status']);
  const accountId = Array.isArray(f['Account']) ? f['Account'][0] : '';
  const linkedLanding = Array.isArray(f['Consulting Landing']) && f['Consulting Landing'].length > 0;
  const meetingStatus = statusName(f[FACT_FIELDS.meetingStatus]);
  const paymentStatus = statusName(f[FACT_FIELDS.paymentStatus]);
  const none = { state: 'none', at: '', source: '', precision: '' };
  const facts = {};
  const dayOnly = dayOnlyFieldsOf(f[FACT_FIELDS.factsNote]);
  const when = (field) => whenOf(f, field, dayOnly);

  facts.formSubmitted = f['Status'] === 'Submitted'
    ? { state: 'confirmed', at: dateOf(f['Submitted At']), precision: dateOf(f['Submitted At']) ? 'moment' : '', source: 'Status' } : none;

  facts.quoteDrafted = (qs === 'Draft' || qs === 'Approved' || qs === 'Won' || quoteHasLines(f['Quote Lines JSON']))
    ? { state: 'confirmed', at: '', source: 'Quote Status' } : none;

  // Won after an approval: the approval happened (no date unless its own field has one)
  facts.quoteApproved = (qs === 'Approved' || qs === 'Won' || has(f[FACT_FIELDS.quoteApprovedAt]))
    ? { state: 'confirmed', ...when(FACT_FIELDS.quoteApprovedAt), source: 'Quote Status' } : none;

  // Meeting: only its date confirms it (Meeting Held At). A status alone never does: a
  // select can come with a default option. Held without a date is pending; Scheduled
  // counts as a signal only with its date (Meeting At). An approved quote never does.
  const held = when(FACT_FIELDS.meetingHeldAt);
  // A scheduled meeting: when it is planned (may be in the future). Never the date it was held.
  const planned = meetingStatus === 'Scheduled' ? when(FACT_FIELDS.meetingAt) : { at: '', precision: '' };
  const plan = planned.at ? { plannedAt: planned.at, plannedPrecision: planned.precision } : {};
  if (held.at) {
    facts.meetingHeld = { state: 'confirmed', ...held, source: FACT_FIELDS.meetingHeldAt };
  } else if (meetingStatus === 'Held') {
    facts.meetingHeld = { state: 'pending', at: '', precision: '', source: 'status-without-date' };
  } else if (meetingStatus === 'Cancelled') {
    facts.meetingHeld = { state: 'cancelled', at: '', precision: '', source: FACT_FIELDS.meetingStatus };
  } else if (planned.at || scheduledMeetingsOf(accountId, outreach).length) {
    facts.meetingHeld = { state: 'pending', at: '', precision: '', source: planned.at ? FACT_FIELDS.meetingAt : 'Outreach', ...plan };
  } else facts.meetingHeld = none;

  facts.proposalPublished = linkedLanding
    ? { state: 'confirmed', at: dateOf(landing?.fields?.['Published At']), precision: dateOf(landing?.fields?.['Published At']) ? 'moment' : '', source: 'Consulting Landing' } : none;

  // Sent: only its own date confirms it. Published (or won) without it: pending, never assumed
  if (has(f[FACT_FIELDS.proposalSentAt])) {
    facts.proposalSent = { state: 'confirmed', ...when(FACT_FIELDS.proposalSentAt), source: FACT_FIELDS.proposalSentAt, via: f[FACT_FIELDS.proposalSentVia] || '' };
  } else if (linkedLanding || qs === 'Won') {
    facts.proposalSent = { state: 'pending', at: '', precision: '', source: linkedLanding ? 'Consulting Landing' : 'Quote Status' };
  } else facts.proposalSent = none;

  // Accepted and later cancelled by the client (Lost after an acceptance): its date stays, never counted as won
  const marks = acceptanceMarksOf(f[FACT_FIELDS.factsNote]);
  if (qs === 'Lost' && (has(f[FACT_FIELDS.wonAt]) || marks.cancelled)) {
    facts.won = { state: 'cancelled', ...when(FACT_FIELDS.wonAt), source: marks.cancelled ? FACT_FIELDS.factsNote : 'Quote Status' };
  } else facts.won = (qs === 'Won' || has(f[FACT_FIELDS.wonAt]))
    ? { state: 'confirmed', ...when(FACT_FIELDS.wonAt), source: 'Quote Status' } : none;

  // Payment and service: their own dates only. A status alone never confirms a payment
  // (a select can come with a default option); Partial counts only on a won quote.
  const paid = when(FACT_FIELDS.paymentReceivedAt);
  if (paid.at) {
    facts.paymentReceived = { state: 'confirmed', ...paid, source: FACT_FIELDS.paymentReceivedAt };
  } else if (paymentStatus === 'Received') {
    facts.paymentReceived = { state: 'pending', at: '', precision: '', source: 'status-without-date' };
  } else if (paymentStatus === 'Partial' && facts.won.state === 'confirmed') {
    facts.paymentReceived = { state: 'partial', at: '', precision: '', source: FACT_FIELDS.paymentStatus };
  } else if (facts.won.state === 'confirmed') {
    facts.paymentReceived = { state: 'pending', at: '', precision: '', source: 'Quote Status' };
  } else facts.paymentReceived = none;

  if (has(f[FACT_FIELDS.serviceStartedAt])) {
    facts.serviceStarted = { state: 'confirmed', ...when(FACT_FIELDS.serviceStartedAt), source: FACT_FIELDS.serviceStartedAt };
  } else if (facts.won.state === 'confirmed') {
    facts.serviceStarted = { state: 'pending', at: '', precision: '', source: 'Quote Status' };
  } else facts.serviceStarted = none;

  return facts;
}

/** The column of a quote: the furthest confirmed fact, never a pending one. */
export function pipelineStageOf(facts) {
  if (facts.won.state === 'confirmed') return 'won';
  if (facts.won.state === 'cancelled') return 'cancelled';
  if (facts.proposalSent.state === 'confirmed') return 'proposal-sent';
  if (facts.proposalPublished.state === 'confirmed') return 'proposal-published';
  if (facts.meetingHeld.state === 'confirmed') return 'meeting-held';
  if (facts.quoteApproved.state === 'confirmed') return 'quote-approved';
  if (facts.quoteDrafted.state === 'confirmed') return 'quote-draft';
  if (facts.formSubmitted.state === 'confirmed') return 'intake-filled';
  return 'intake-sent';
}

export const stageIndex = (key) => STAGE_INDEX[key] ?? 0;

const FACT_LABELS = {
  meetingHeld: { confirmed: 'Reunión realizada', pending: 'Reunión: pendiente de confirmar', cancelled: 'Reunión cancelada' },
  proposalPublished: { confirmed: 'Propuesta publicada' },
  proposalSent: { confirmed: 'Propuesta enviada', pending: 'Envío: pendiente de confirmar' },
  won: { confirmed: 'Ganada', cancelled: 'Aceptó y luego canceló' },
  paymentReceived: { confirmed: 'Pago recibido', partial: 'Pago parcial', pending: 'Pago: pendiente de confirmar' },
  serviceStarted: { confirmed: 'Servicio iniciado', pending: 'Inicio: pendiente de confirmar' },
};

/**
 * What the card shows besides its column: the facts that are not implied by
 * it, each with its state and date (only a real one). Facts without any signal
 * are left out, except the ones a won quote still needs (payment and start).
 */
export function factBadgesOf(facts) {
  const out = [];
  for (const key of ['meetingHeld', 'proposalPublished', 'proposalSent', 'won', 'paymentReceived', 'serviceStarted']) {
    const fact = facts[key];
    const label = FACT_LABELS[key]?.[fact.state];
    if (!label) continue;
    const scheduled = key === 'meetingHeld' && fact.state === 'pending' && fact.plannedAt;
    out.push({
      key, state: fact.state, at: fact.at, precision: fact.precision || '', why: whyOf(key, fact),
      // A scheduled meeting shows when it is planned, apart from the (pending) fact
      label: scheduled ? 'Reunión agendada' : label,
      ...(scheduled ? { plannedAt: fact.plannedAt, plannedPrecision: fact.plannedPrecision } : {}),
    });
  }
  return out;
}

function whyOf(key, fact) {
  if (fact.state === 'confirmed' && !fact.at) return 'Confirmado, sin fecha registrada (dato anterior a este registro).';
  if (fact.state !== 'pending') return '';
  if (fact.source === 'status-without-date') return 'El estado está marcado, pero falta su fecha: confirmalo registrando cuándo ocurrió.';
  if (key === 'meetingHeld' && fact.plannedAt) return 'Agendada: cuando ocurra, registrá que se realizó.';
  if (key === 'meetingHeld') return fact.source === 'Outreach' ? 'Hay una reunión agendada en la actividad de la empresa; no consta que se haya realizado.' : 'La reunión está agendada; no consta que se haya realizado.';
  if (key === 'proposalSent') return fact.source === 'Consulting Landing' ? 'La propuesta está publicada; no consta cuándo ni cómo se envió.' : 'Está ganada, pero no consta el envío de la propuesta.';
  if (key === 'paymentReceived') return 'Está ganada; el pago no está registrado.';
  if (key === 'serviceStarted') return 'Está ganada; el inicio del servicio no está registrado.';
  return '';
}

/** The date field each fact is written to. */
export const FACT_DATE_FIELD = {
  meetingHeld: FACT_FIELDS.meetingHeldAt, proposalSent: FACT_FIELDS.proposalSentAt, won: FACT_FIELDS.wonAt,
  paymentReceived: FACT_FIELDS.paymentReceivedAt, serviceStarted: FACT_FIELDS.serviceStartedAt,
};

/**
 * The fields to write when Ale registers a fact that already happened. `at` is the
 * day she chose (YYYY-MM-DD); `time` (HH:MM) is optional. Never in the future: these
 * are facts that already happened (checked here, not only in the date picker).
 * Without a time, Pipeline Facts Note keeps the "solo el día" mark; with one, the mark goes.
 */
export function confirmFactFields(key, { at, time = '', via = '', note = '', today = localToday(), now = new Date() } = {}) {
  const field = FACT_DATE_FIELD[key];
  if (!field) throw new Error(`Hecho desconocido: ${key}`);
  const w = encodeWhen(at, time);
  if (w.day ? w.day > today : w.moment.getTime() > now.getTime() + 5 * 60_000) {
    throw new Error('La fecha no puede ser futura: registrá un hecho que ya ocurrió.');
  }
  const fields = { [field]: w.iso, [FACT_FIELDS.factsNote]: noteWithDay(note, field, w.day) };
  if (key === 'meetingHeld') fields[FACT_FIELDS.meetingStatus] = 'Held';
  if (key === 'proposalSent' && via) fields[FACT_FIELDS.proposalSentVia] = String(via).slice(0, 80);
  if (key === 'won') fields['Quote Status'] = 'Won';
  if (key === 'paymentReceived') fields[FACT_FIELDS.paymentStatus] = 'Received';
  return fields;
}

/** Scheduling a meeting: a plan, not a fact, so it may be in the future. It never confirms the meeting. */
export function scheduleMeetingFields({ at, time = '', note = '' } = {}) {
  const w = encodeWhen(at, time);
  return {
    [FACT_FIELDS.meetingStatus]: 'Scheduled', [FACT_FIELDS.meetingAt]: w.iso,
    [FACT_FIELDS.factsNote]: noteWithDay(note, FACT_FIELDS.meetingAt, w.day),
  };
}
